import { EyeOutlined, SaveOutlined, UndoOutlined } from "@ant-design/icons";
import { Button, Card, Checkbox, Col, Form, Input, Radio, Row, Select, Space, Spin, Switch, Tag, Tooltip, Typography, message } from "antd";
import dayjs from "dayjs";
import { useCallback, useEffect, useState } from "react";
import { receiptAPIWithErrorHandling } from "../../../api/receipt/receipt";
import { confirmAction } from "../../../components/common/confirmDialog";
import BrandedPageLayout from "../../../components/Dashboard/BrandedPageLayout";
import { border, radius, shadow, spacing, typography } from "../../../theme/tokens";

const { Text } = Typography;

/** Gợi ý cho ô ký, người dùng vẫn gõ được tên khác. */
const SIGNATURE_OPTIONS = ["Người lập phiếu", "Người nộp tiền", "Thủ quỹ", "Kế toán trưởng"].map((value) => ({
    value,
    label: value,
}));

const cardStyle = { height: "100%", borderRadius: radius.md, border: border.hairline, boxShadow: shadow.xs };

const CardTitle = ({ children }) => (
    <span style={{ fontSize: typography.size.md, fontWeight: typography.weight.semibold }}>{children}</span>
);

/**
 * Cấu hình phiếu thu của chủ đợt thu.
 *
 * Áp cho mọi phiếu của đơn vị: in lẻ, xuất hàng loạt và trang phiếu cư dân mở từ tin nhắn.
 * Chưa lưu lần nào thì backend trả mẫu mặc định (mẫu phường đang chạy), nên form mở ra là
 * đúng phiếu hiện tại, sửa từ đó. Xem thử dựng từ dữ liệu đang sửa, không cần lưu trước.
 */
const ReceiptSetting = () => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [previewing, setPreviewing] = useState(false);
    const [meta, setMeta] = useState({ isDefault: true, updatedAt: null });
    // Tắt dòng địa chỉ thì cách ghi địa chỉ không còn tác dụng.
    const showAddress = Form.useWatch("show_address", form);

    const applyResponse = useCallback(
        (data) => {
            form.setFieldsValue(data.setting);
            setMeta({ isDefault: data.is_default, updatedAt: data.updated_at });
        },
        [form]
    );

    useEffect(() => {
        const load = async () => {
            try {
                const res = await receiptAPIWithErrorHandling.getSettings();
                applyResponse(res.data);
            } catch (error) {
                message.error(error.message);
            } finally {
                setLoading(false);
            }
        };
        load();
    }, [applyResponse]);

    const handleSave = async (values) => {
        try {
            setSaving(true);
            const res = await receiptAPIWithErrorHandling.updateSettings(values);
            applyResponse(res.data);
            message.success(res.message);
        } catch (error) {
            message.error(error.message);
        } finally {
            setSaving(false);
        }
    };

    const handlePreview = async () => {
        let values;
        try {
            values = await form.validateFields();
        } catch {
            return;
        }

        // Mở tab ngay trong cú bấm: mở sau khi chờ API thì trình duyệt coi là popup và chặn.
        const tab = window.open("", "_blank");
        try {
            setPreviewing(true);
            const blob = await receiptAPIWithErrorHandling.previewSettings(values);
            const url = URL.createObjectURL(new Blob([blob], { type: "application/pdf" }));

            if (tab) {
                tab.location.href = url;
            } else {
                message.warning("Vui lòng cho phép popup để xem phiếu mẫu!");
            }
            setTimeout(() => URL.revokeObjectURL(url), 60000);
        } catch (error) {
            tab?.close();
            message.error(error.message);
        } finally {
            setPreviewing(false);
        }
    };

    const handleReset = async () => {
        const confirmed = await confirmAction({
            title: "Khôi phục mẫu phiếu mặc định?",
            content:
                "Phiếu sẽ in lại theo mẫu phường như trước khi cấu hình: tiêu đề PHIẾU THU, Mẫu số C45-BB, " +
                "Mã QHNS, 2 ô ký, không in hạn thanh toán. Tên đơn vị phát hành lấy theo Cài đặt tài khoản.",
            okText: "Khôi phục",
            okType: "danger",
        });
        if (!confirmed) return;

        try {
            setSaving(true);
            const res = await receiptAPIWithErrorHandling.resetSettings();
            applyResponse(res.data);
            message.success(res.message);
        } catch (error) {
            message.error(error.message);
        } finally {
            setSaving(false);
        }
    };

    const statusTag = meta.isDefault ? (
        <Tag>Đang dùng mẫu mặc định</Tag>
    ) : (
        <Tooltip title={meta.updatedAt ? `Cập nhật lúc ${dayjs(meta.updatedAt).format("DD/MM/YYYY HH:mm")}` : null}>
            <Tag color="processing">Đã cấu hình riêng</Tag>
        </Tooltip>
    );

    return (
        <BrandedPageLayout
            title="Cấu hình phiếu thu"
            subtitle="Áp dụng cho mọi phiếu thu của đơn vị: in lẻ, xuất hàng loạt và trang phiếu cư dân mở từ tin nhắn."
            breadcrumbItems={[{ title: "Cài đặt" }, { title: "Cấu hình phiếu thu" }]}
            extra={statusTag}
        >
            <Spin spinning={loading}>
                <Form form={form} layout="vertical" onFinish={handleSave} requiredMark={false}>
                    <Row gutter={[spacing[6], spacing[6]]}>
                        <Col xs={24} lg={12}>
                            <Card title={<CardTitle>Đầu phiếu</CardTitle>} style={cardStyle}>
                                <Form.Item
                                    name="parent_agency"
                                    label="Cơ quan chủ quản"
                                    extra="In phía trên tên đơn vị phát hành, ví dụ: ỦY BAN NHÂN DÂN PHƯỜNG ... Để trống thì không in."
                                >
                                    <Input maxLength={255} />
                                </Form.Item>
                                <Form.Item
                                    name="issuer_name"
                                    label="Tên đơn vị phát hành"
                                    extra="Dòng in đậm trên cùng, ví dụ tên UBND phường hoặc ban quản lý. Để trống thì không in."
                                >
                                    <Input maxLength={255} placeholder="Ví dụ: UBND PHƯỜNG ..." />
                                </Form.Item>
                                <Form.Item name="issuer_address" label="Địa chỉ đơn vị" extra="Để trống thì không in.">
                                    <Input maxLength={255} />
                                </Form.Item>
                                <Form.Item name="tax_code" label="Mã số thuế" extra="Để trống thì không in.">
                                    <Input maxLength={50} />
                                </Form.Item>
                                <Form.Item
                                    name="unit_label"
                                    label="Nhãn đơn vị"
                                    extra={'In trước tên đơn vị cấp 1, ví dụ "Đơn vị: Khu phố 24" hoặc "Khu vực: An Dương Vương".'}
                                    rules={[{ required: true, whitespace: true, message: "Vui lòng nhập nhãn đơn vị!" }]}
                                >
                                    <Input maxLength={50} />
                                </Form.Item>
                                <Form.Item
                                    name="show_budget_code"
                                    label="In Mã QHNS"
                                    valuePropName="checked"
                                    extra="Lấy theo mã ngân sách của đơn vị cấp 1. Đơn vị chưa có mã thì tự ẩn dòng."
                                >
                                    <Switch />
                                </Form.Item>
                                <Form.Item
                                    name="show_national_title"
                                    label="In quốc hiệu, tiêu ngữ"
                                    valuePropName="checked"
                                    extra="CỘNG HÒA XÃ HỘI CHỦ NGHĨA VIỆT NAM - Độc lập - Tự do - Hạnh phúc, ở góc phải đầu phiếu. Phường nên bật, doanh nghiệp nên tắt."
                                >
                                    <Switch />
                                </Form.Item>
                                <Form.Item
                                    name="show_form_code"
                                    label="In Mẫu số C45-BB (TT 107/2017/TT-BTC)"
                                    valuePropName="checked"
                                    extra="Mẫu của kế toán hành chính sự nghiệp, dành cho phường và đơn vị công. Doanh nghiệp nên tắt."
                                    style={{ marginBottom: 0 }}
                                >
                                    <Switch />
                                </Form.Item>
                            </Card>
                        </Col>

                        <Col xs={24} lg={12}>
                            <Card title={<CardTitle>Nội dung phiếu</CardTitle>} style={cardStyle}>
                                <Form.Item
                                    name="title"
                                    label="Tiêu đề phiếu"
                                    extra="Ví dụ: PHIẾU THU (phường), THÔNG BÁO PHÍ (ban quản lý)."
                                    rules={[{ required: true, whitespace: true, message: "Vui lòng nhập tiêu đề phiếu!" }]}
                                >
                                    <Input maxLength={100} />
                                </Form.Item>
                                {/* Liệt kê theo đúng thứ tự trên phiếu để người cấu hình hình dung được ngay. */}
                                <Form.Item label="Thông tin người nộp in trên phiếu" extra="Họ tên và số tiền luôn được in.">
                                    <Space direction="vertical" size={spacing[1]}>
                                        <Checkbox checked disabled>
                                            Họ và tên người nộp tiền
                                        </Checkbox>
                                        <Form.Item name="show_address" valuePropName="checked" noStyle>
                                            <Checkbox>Địa chỉ / căn hộ</Checkbox>
                                        </Form.Item>
                                        <Form.Item name="show_content" valuePropName="checked" noStyle>
                                            <Checkbox>Nội dung chuyển khoản</Checkbox>
                                        </Form.Item>
                                        <Checkbox checked disabled>
                                            Số tiền
                                        </Checkbox>
                                        <Form.Item name="show_amount_text" valuePropName="checked" noStyle>
                                            <Checkbox>Số tiền viết bằng chữ</Checkbox>
                                        </Form.Item>
                                        <Form.Item name="show_deadline" valuePropName="checked" noStyle>
                                            <Checkbox>
                                                Hạn thanh toán{" "}
                                                <Text type="secondary">(theo "Hạn thu (tin nhắn nhận)" của đợt thu)</Text>
                                            </Checkbox>
                                        </Form.Item>
                                    </Space>
                                </Form.Item>
                                <Form.Item name="address_mode" label="Cách ghi địa chỉ người nộp">
                                    <Radio.Group disabled={showAddress === false}>
                                        <Space direction="vertical" size={spacing[2]}>
                                            <Radio value="asset">
                                                Địa chỉ tài sản
                                                <div>
                                                    <Text type="secondary">Địa chỉ: 272/1/6 Lê Đức Thọ</Text>
                                                </div>
                                            </Radio>
                                            <Radio value="apartment">
                                                Căn hộ
                                                <div>
                                                    <Text type="secondary">Căn hộ: TT1-KD-06-02 · 226 An Dương Vương</Text>
                                                </div>
                                            </Radio>
                                        </Space>
                                    </Radio.Group>
                                </Form.Item>
                                <Form.Item
                                    name="signatures"
                                    label="Ô ký"
                                    extra="Chọn hoặc gõ tên ô ký, tối đa 4 ô. Để trống nếu phiếu không cần ký, ví dụ thông báo phí."
                                >
                                    <Select
                                        mode="tags"
                                        options={SIGNATURE_OPTIONS}
                                        maxCount={4}
                                        tokenSeparators={[","]}
                                        placeholder="Không có ô ký"
                                    />
                                </Form.Item>
                                {/* 500 ký tự khớp giới hạn backend: phiếu bật đủ mọi thứ vẫn vừa một trang A4. */}
                                <Form.Item
                                    name="footer_note"
                                    label="Ghi chú cuối phiếu"
                                    extra="In chữ nhỏ ở cuối phiếu, mỗi dòng một ý: chú thích, hotline, hướng dẫn nộp tiền... Để trống thì không in."
                                    style={{ marginBottom: 0 }}
                                >
                                    <Input.TextArea maxLength={500} showCount autoSize={{ minRows: 3, maxRows: 8 }} />
                                </Form.Item>
                            </Card>
                        </Col>
                    </Row>

                    <div
                        style={{
                            display: "flex",
                            flexWrap: "wrap",
                            justifyContent: "space-between",
                            gap: spacing[3],
                            marginTop: spacing[6],
                        }}
                    >
                        <Button icon={<UndoOutlined />} onClick={handleReset} disabled={meta.isDefault || loading || saving}>
                            Khôi phục mặc định
                        </Button>
                        <Space wrap>
                            <Tooltip title="Dựng phiếu từ nội dung đang sửa trên một khoản thu thật, chưa cần lưu.">
                                <Button icon={<EyeOutlined />} onClick={handlePreview} loading={previewing} disabled={loading}>
                                    Xem thử phiếu
                                </Button>
                            </Tooltip>
                            <Button type="primary" htmlType="submit" icon={<SaveOutlined />} loading={saving} disabled={loading}>
                                Lưu cấu hình
                            </Button>
                        </Space>
                    </div>
                </Form>
            </Spin>
        </BrandedPageLayout>
    );
};

export default ReceiptSetting;
